import produce from 'immer';

import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { AnyFilter, Fetcher } from '../../../shared/organizationPackages/filters';
import type {
  FetchPackageDetailsResponse,
  FetchPackageMetadataResponse,
  FetchPackagesResponse,
  FetchProjectsResponse,
  FetchVersionDetailsResponse,
  PackageDetails,
  PackageSummary,
  PackageVersionVulnerability,
  SelectedVuln,
} from '../../../shared/organizationPackages/response';
import type { SortOption } from '../../../shared/organizationPackages/sortOptions';
import type { MinimalWeakness } from '../../../shared/vulns/types';
import type { ReduxAction, ReduxActionResponse, SyncReduxAction } from '../../types';

import { asyncActionTypes, syncActionTypes } from './actions';
import { setFilters } from './utils';
import type { HydrateStateQuery, PackageInventoryStore } from './types';

export const initialState: PackageInventoryStore = {
  /**
   * Manages the state of all the various network requests.
   */
  asyncStatus: {
    getPackages: getInitialStatus(),
    getPackageDetails: getInitialStatus(),
    getPackageProjects: getInitialStatus(),
    getPackageMetadata: getInitialStatus(),
    getPackageVersionMetadata: getInitialStatus(),
    getPackageManagers: getInitialStatus(),
    getPackageLocators: getInitialStatus(),
    getPackageSummary: getInitialStatus(),
    getCwes: getInitialStatus(),
    getPackageVulnerabilities: getInitialStatus(),
    getSelectedVuln: getInitialStatus(),
    blockPackageVersions: getInitialStatus(),
  },

  /**
   * List of fetched packages.
   */
  packages: [],

  /**
   * Total projects count (without pagination).
   */
  totalProjectsCount: 0,

  /**
   * Package Details for the selected package
   */
  details: null,

  /**
   * Package metadata for the selected package.
   */
  packageMetadata: null,

  /**
   * Metadata about the package version, for the PackageVersion page.
   */
  packageVersionMetadata: null,

  /**
   * Projects is the list of user projects associated with the package version
   */
  projects: null,

  /**
   * Vulnerabilities associated with the package version
   */
  vulnerabilities: null,

  /**
   * This represents the "selected" vuln, ie: the one which is clicked on and appears in the Drawer.
   */
  selectedVuln: null,

  /**
   * Total package count (un-paginated)
   */
  totalPackagesCount: 0,

  /**
   * Applied filters
   */
  filter: {
    //  PackageFilter
    fetchers: [],
    packageLabels: [],
    packageName: '',
    //  ProjectFilter
    depth: [],
    labels: [],
    projectName: '',
    sources: [],
    visibility: [],
    blockTypes: [],
    //  VulnFilter
    cve: '',
    cwes: [],
    fixTypes: [],
    severities: [],
  },

  /**
   * Pagination state
   */
  pagination: {
    packages: {
      page: 1,
      count: 20,
    },
    versionDetails: {
      page: 1,
      count: 20,
    },
  },

  /**
   * Sorting state
   */
  sort: null,

  /**
   * This represents the list of fetchers which the organization
   * actually uses, fetched from `GET /api/packages/package-managers`
   */
  packageManagers: [],

  /**
   * the list of package locators for the package locator filter
   * fetched from `GET /api/packages/package-locators`
   */
  locators: [],

  /**
   * A list of cwes for the cwe filter dropdown
   */
  cwes: [],

  /**
   * Selected versions in the package details table, used for the denial workflow.
   */
  selectedVersions: [],
  /**
   * Package summary (count and last cache date) for the package index CTA.
   */
  packageSummary: null,
};

const resetPackagesPage = (draftState: PackageInventoryStore) => {
  draftState.pagination.packages.page = initialState.pagination.packages.page;
};

const resetVersionDetailsPage = (draftState: PackageInventoryStore) => {
  draftState.pagination.versionDetails.page = initialState.pagination.versionDetails.page;
};

const resetPackagesSidebar = (draftState: PackageInventoryStore) => {
  draftState.packages = initialState.packages;
};

const resetPackageDetails = (draftState: PackageInventoryStore) => {
  draftState.details = initialState.details;
};

const resetPackageMetadata = (draftState: PackageInventoryStore) => {
  draftState.packageMetadata = initialState.packageMetadata;
};

const resetVersionDetails = (draftState: PackageInventoryStore) => {
  draftState.packageVersionMetadata = null;
  draftState.projects = null;
};

const resetSelectedVersions = (draftState: PackageInventoryStore) => {
  draftState.selectedVersions = initialState.selectedVersions;
};

const fetchPackagesReducers = {
  [asyncActionTypes.GET_PACKAGES.PENDING]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackages = getPendingStatus();
    }),

  [asyncActionTypes.GET_PACKAGES.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<FetchPackagesResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackages = getInitialStatus();
      if (action.payload.data) {
        const { data, count } = action.payload.data;
        draftState.packages = data;
        draftState.totalPackagesCount = count;
      }
    }),
  [asyncActionTypes.GET_PACKAGES.REJECTED]: (state: PackageInventoryStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackages = getErrorStatus(action.payload);
    }),
};

const fetchPackageDetailsReducer = {
  [asyncActionTypes.GET_PACKAGE_DETAILS.PENDING]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageDetails = getPendingStatus();
      resetSelectedVersions(draftState);
    }),

  [asyncActionTypes.GET_PACKAGE_DETAILS.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<FetchPackageDetailsResponse>,
  ) =>
    produce(state, (draftState) => {
      // Compare the package locator in the API response with the current URL
      // to ensure the correct response is added to Redux.
      // This prevents users from seeing old results when they click too fast.
      const apiPathname = new URL(action.payload.url).pathname;
      const apiPathnameParts = apiPathname.split('/');
      const apiPackageLocator = apiPathnameParts[3];
      const urlPathnameParts = window.location.pathname.split('/');
      const urlPackageLocator = urlPathnameParts[2];

      if (apiPackageLocator === urlPackageLocator) {
        draftState.asyncStatus.getPackageDetails = getInitialStatus();
        draftState.details = action.payload?.data ?? null;
      }
    }),
  [asyncActionTypes.GET_PACKAGE_DETAILS.REJECTED]: (state: PackageInventoryStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageDetails = getErrorStatus(action.payload);
    }),
  [syncActionTypes.RESET_PACKAGE_DETAILS]: (state: PackageInventoryStore) => produce(state, (draftState) => {
    resetPackageDetails(draftState);
    resetPackageMetadata(draftState);
    //  Also set the loading state to true to avoid seeing the "no versions found" fallback UI
    draftState.asyncStatus.getPackageDetails = getPendingStatus();
    draftState.asyncStatus.getPackageMetadata = getPendingStatus();
  }),
  [syncActionTypes.SET_PACKAGE_DETAILS]: (state: PackageInventoryStore, action: SyncReduxAction<{ details: PackageDetails[] | null }>) =>
    produce(state, (draftState) => {
      draftState.details = action.details;
      resetSelectedVersions(draftState);
    }),
};

const fetchPackageProjectsReducer = {
  [asyncActionTypes.GET_PACKAGE_PROJECTS.PENDING]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageProjects = getPendingStatus();
      draftState.projects = [];
    }),

  [asyncActionTypes.GET_PACKAGE_PROJECTS.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<FetchProjectsResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageProjects = getInitialStatus();
      if (action.payload.data) {
        const { data, count } = action.payload.data;
        draftState.projects = data;
        draftState.totalProjectsCount = count;
      }
    }),
  [asyncActionTypes.GET_PACKAGE_PROJECTS.REJECTED]: (state: PackageInventoryStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageProjects = getErrorStatus(action.payload);
    }),
};

const fetchPackageMetadataReducer = {
  [asyncActionTypes.GET_PACKAGE_METADATA.PENDING]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageMetadata = getPendingStatus();
    }),
  [asyncActionTypes.GET_PACKAGE_METADATA.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<FetchPackageMetadataResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageMetadata = getInitialStatus();
      if (action.payload.data) {
        draftState.packageMetadata = action.payload.data;
      }
    }),
  [asyncActionTypes.GET_PACKAGE_METADATA.REJECTED]: (state: PackageInventoryStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageMetadata = getErrorStatus(action.payload);
    }),
};

const fetchPackageVersionMetadataReducer = {
  [asyncActionTypes.GET_PACKAGE_VERSION_METADATA.PENDING]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageVersionMetadata = getPendingStatus();
    }),
  [asyncActionTypes.GET_PACKAGE_VERSION_METADATA.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<FetchVersionDetailsResponse>,
  ) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageVersionMetadata = getInitialStatus();
      draftState.packageVersionMetadata = action.payload?.data || null;
    }),
  [asyncActionTypes.GET_PACKAGE_VERSION_METADATA.REJECTED]: (state: PackageInventoryStore, action: ReduxAction) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackageVersionMetadata = getErrorStatus(action.payload);
    }),
  [syncActionTypes.RESET_PACKAGE_VERSION_DETAILS]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      resetVersionDetails(draftState);
    }),
};

const hydrateQueryStateReducer = {
  [syncActionTypes.HYDRATE_PACKAGES_QUERY_STATE]: (
    state: PackageInventoryStore,
    action: SyncReduxAction<{ query: HydrateStateQuery }>,
  ) =>
    produce(state, (draftState) => {
      //  Note: everything has already been validated by parseQueryParams, in `app/helpers/packageInventory.ts`
      const { query } = action;

      if (query.page) {
        draftState.pagination.packages.page = query.page;
      }

      if (query.count) {
        draftState.pagination.packages.count = query.count;
      }

      if (query.sort) {
        draftState.sort = query.sort;
      }

      setFilters(draftState, query.filter);
    }),
  [syncActionTypes.HYDRATE_VERSION_DETAILS_QUERY_STATE]: (
    state: PackageInventoryStore,
    action: SyncReduxAction<{ query: HydrateStateQuery }>,
  ) =>
    produce(state, (draftState) => {
      //  Note: everything has already been validated by parseQueryParams, in `app/helpers/packageInventory.ts`
      const { query } = action;

      if (query.page) {
        draftState.pagination.versionDetails.page = query.page;
      }

      if (query.count) {
        draftState.pagination.versionDetails.count = query.count;
      }

      setFilters(draftState, query.filter);
    }),
  [syncActionTypes.PRE_FETCH_LOADING]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      draftState.asyncStatus.getPackages = getPendingStatus();
      resetPackagesSidebar(draftState);
      resetPackageDetails(draftState);
      resetPackageMetadata(draftState);
    }),
};

const paginationReducers = {
  [syncActionTypes.SET_PACKAGES_PAGE]: (state: PackageInventoryStore, action: SyncReduxAction<{ page: number }>) =>
    produce(state, (draftState) => {
      draftState.pagination.packages.page = action.page;
      resetPackageDetails(draftState);
    }),
  [syncActionTypes.SET_PACKAGES_COUNT]: (state: PackageInventoryStore, action: SyncReduxAction<{ count: number }>) =>
    produce(state, (draftState) => {
      draftState.pagination.packages.count = action.count;
      // resetting the page so users don't end up in an unreachable page.
      resetPackagesPage(draftState);
      resetPackageDetails(draftState);
    }),
  [syncActionTypes.SET_VERSION_DETAILS_PAGE]:
    (state: PackageInventoryStore, action: SyncReduxAction<{ page: number }>) =>
      produce(state, (draftState) => {
        draftState.pagination.versionDetails.page = action.page;
      }),
  [syncActionTypes.SET_VERSION_DETAILS_COUNT]:
    (state: PackageInventoryStore, action: SyncReduxAction<{ count: number }>) =>
      produce(state, (draftState) => {
        draftState.pagination.versionDetails.count = action.count;
        // resetting the page so users don't end up in an unreachable page.
        resetVersionDetailsPage(draftState);
      }),
  [syncActionTypes.RESET_PACKAGES_PAGE]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      resetPackagesPage(draftState);
    }),
  [syncActionTypes.RESET_VERSION_DETAILS_PAGE]: (state: PackageInventoryStore) =>
    produce(state, (draftState) => {
      resetVersionDetailsPage(draftState);
    }),
};

const sortReducer = {
  [syncActionTypes.SET_PACKAGES_SORT]: (state: PackageInventoryStore, action: SyncReduxAction<{ sort: SortOption }>) =>
    produce(state, draftState => {
      draftState.sort = action.sort;
    }),
};

const filtersReducer = {
  [syncActionTypes.RESET_FILTERS]: (state: PackageInventoryStore) =>
    produce(state, draftState => {
      draftState.filter = initialState.filter;
      resetPackagesPage(draftState);
    }),
  [syncActionTypes.SET_FILTERS]: (
    state: PackageInventoryStore,
    action: SyncReduxAction<{ filters: AnyFilter }>,
  ) => produce(state, draftState => {
    setFilters(draftState, action.filters);
    resetPackagesPage(draftState);
    resetPackagesSidebar(draftState);
    resetPackageDetails(draftState);
  }),
};

/**
 * If this request fails, the UI will continue to work,
 * simply displaying all packagae managers instead of the ones actually used.
 */
const fetchPackageManagersReducer = {
  [asyncActionTypes.GET_PACKAGE_MANAGERS.PENDING]: (state: PackageInventoryStore) => produce(state, draftState => {
    draftState.asyncStatus.getPackageManagers = getPendingStatus();
  }),
  [asyncActionTypes.GET_PACKAGE_MANAGERS.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<Fetcher[]>,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getPackageManagers = getInitialStatus();
    if (action.payload.data) {
      draftState.packageManagers = action.payload.data;
    }
  }),
  [asyncActionTypes.GET_PACKAGE_MANAGERS.REJECTED]: (
    state: PackageInventoryStore,
    action: ReduxAction,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getPackageManagers = getErrorStatus(action.payload);
  }),
};

const fetchPackageLocatorsReducer = {
  [asyncActionTypes.GET_PACKAGE_LOCATORS.PENDING]: (state: PackageInventoryStore) => produce(state, draftState => {
    draftState.asyncStatus.getPackageLocators = getPendingStatus();
  }),
  [asyncActionTypes.GET_PACKAGE_LOCATORS.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<{ results: string[] }>,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getPackageLocators = getInitialStatus();
    draftState.locators = action.payload.data?.results || [];
  }),
  [asyncActionTypes.GET_PACKAGE_LOCATORS.REJECTED]: (
    state: PackageInventoryStore,
    action: ReduxAction,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getPackageLocators = getErrorStatus(action.payload);
  }),
};

const fetchCwesReducer = {
  [asyncActionTypes.GET_CWES.PENDING]: (state: PackageInventoryStore) => produce(state, draftState => {
    draftState.asyncStatus.getCwes = getPendingStatus();
  }),
  [asyncActionTypes.GET_CWES.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<MinimalWeakness[]>,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getCwes = getInitialStatus();
    if (action.payload.data) {
      draftState.cwes = action.payload.data;
    }
  }),
  [asyncActionTypes.GET_CWES.REJECTED]: (
    state: PackageInventoryStore,
    action: ReduxAction,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getCwes = getErrorStatus(action.payload);
  }),
};

const fetchPackageVulnerabilitiesReducer = {
  [asyncActionTypes.GET_PACKAGE_VULNERABILITIES.PENDING]:
    (state: PackageInventoryStore) => produce(state, draftState => {
      draftState.asyncStatus.getPackageVulnerabilities = getPendingStatus();
    }),
  [asyncActionTypes.GET_PACKAGE_VULNERABILITIES.FULFILLED]: (
    state: PackageInventoryStore,
    action: ReduxActionResponse<PackageVersionVulnerability[]>,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getPackageVulnerabilities = getInitialStatus();
    draftState.vulnerabilities = action.payload?.data ?? null;
  }),
  [asyncActionTypes.GET_PACKAGE_VULNERABILITIES.REJECTED]: (
    state: PackageInventoryStore,
    action: ReduxAction,
  ) => produce(state, draftState => {
    draftState.asyncStatus.getPackageVulnerabilities = getErrorStatus(action.payload);
  }),
};

const selectedVulnReducer = {
  [syncActionTypes.RESET_SELECTED_VULN]: (state: PackageInventoryStore) => produce(state, draftState => {
    draftState.selectedVuln = initialState.selectedVuln;
  }),
  [asyncActionTypes.GET_SELECTED_VULN.PENDING]:
    (state: PackageInventoryStore) => produce(state, draftState => {
      draftState.asyncStatus.getSelectedVuln = getPendingStatus();
      draftState.selectedVuln = initialState.selectedVuln;
    }),
  [asyncActionTypes.GET_SELECTED_VULN.FULFILLED]:
    (state: PackageInventoryStore, action: ReduxActionResponse<SelectedVuln>) =>
      produce(state, draftState => {
        draftState.asyncStatus.getSelectedVuln = getInitialStatus();
        draftState.selectedVuln = action.payload?.data ?? null;
      }),
  [asyncActionTypes.GET_SELECTED_VULN.REJECTED]:
    (state: PackageInventoryStore, action: ReduxAction) => produce(state, draftState => {
      draftState.asyncStatus.getSelectedVuln = getErrorStatus(action.payload);
    }),
};

const selectedVersionsReducer = {
  [syncActionTypes.TOGGLE_VERSION_SELECTED]: (state: PackageInventoryStore, action: SyncReduxAction<{ packageRevision: PackageDetails['packageRevision'] }>) =>
    produce(state, draftState => {
      const matchesActionRevision = (details: PackageDetails) => details.packageRevision === action.packageRevision;
      //  Remove the version from the array of selectedVersions
      if (draftState.selectedVersions.find(matchesActionRevision)) {
        draftState.selectedVersions = draftState.selectedVersions.filter(details => !matchesActionRevision(details));
      } else {
        //  TODO: maybe we don't need to store the whole object?
        const selectedVersion = draftState.details?.find(matchesActionRevision);
        if (selectedVersion) {
          draftState.selectedVersions = draftState.selectedVersions.concat(selectedVersion);
        }
      }
    }),
  [syncActionTypes.TOGGLE_SELECTED_ALL_VERSIONS]: (state: PackageInventoryStore) =>
    produce(state, draftState => {
      //  TODO: Not sure if this behaviour is correct but we can fix it later
      //  If some are selected, deselect all of them
      //  Otherwise, select them all.
      if (draftState.selectedVersions.length > 0) {
        draftState.selectedVersions = initialState.selectedVersions;
      } else if (draftState.details) { // <- If check ensures that draftState.details isn't null, though it shouldn't be if this is dispatched.
        //  Cloning the array here to avoid referential equality. It probably doesn't matter, but I'm not sure how immer's produce works here.
        draftState.selectedVersions = [...draftState.details];
      }
    }),
  [asyncActionTypes.BLOCK_PACKAGE_VERSIONS.PENDING]: (state: PackageInventoryStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.blockPackageVersions = getPendingStatus();
    }),
  [asyncActionTypes.BLOCK_PACKAGE_VERSIONS.FULFILLED]: (state: PackageInventoryStore) =>
    produce(state, draftState => {
      draftState.asyncStatus.blockPackageVersions = getInitialStatus();
    }),
  [asyncActionTypes.BLOCK_PACKAGE_VERSIONS.REJECTED]: (state: PackageInventoryStore, action: ReduxAction) =>
    produce(state, draftState => {
      draftState.asyncStatus.blockPackageVersions = getErrorStatus(action.payload);
    }),
};

const fetchPackageSummaryReducers = {
  [asyncActionTypes.GET_PACKAGE_SUMMARY.FULFILLED]:
    (state: PackageInventoryStore, action: ReduxActionResponse<PackageSummary>) =>
      produce(state, draftState => {
        draftState.asyncStatus.getPackageSummary.isLoading = false;
        if (action.payload.data) {
          draftState.packageSummary = action.payload.data;
        }
      }),
  [asyncActionTypes.GET_PACKAGE_SUMMARY.PENDING]:
    (state: PackageInventoryStore) =>
      produce(state, draftState => {
        draftState.asyncStatus.getPackageSummary.isLoading = true;
      }),
  [asyncActionTypes.GET_PACKAGE_SUMMARY.REJECTED]:
    (state: PackageInventoryStore, action: ReduxAction) =>
      produce(state, draftState => {
        draftState.asyncStatus.getPackageSummary = getErrorStatus(action.payload);
      }),
};

export default createReducer<PackageInventoryStore>(initialState, {
  ...fetchPackagesReducers,
  ...hydrateQueryStateReducer,
  ...paginationReducers,
  ...sortReducer,
  ...filtersReducer,
  ...fetchPackageDetailsReducer,
  ...fetchPackageProjectsReducer,
  ...fetchPackageMetadataReducer,
  ...fetchPackageVersionMetadataReducer,
  ...fetchPackageManagersReducer,
  ...fetchPackageLocatorsReducer,
  ...fetchCwesReducer,
  ...fetchPackageVulnerabilitiesReducer,
  ...selectedVulnReducer,
  ...selectedVersionsReducer,
  ...fetchPackageSummaryReducers,
});
